import React, { useState } from 'react'

import { ContactModalType } from 'views/Authorized/views/Customers/types'
import CustomDialog from 'shared/components/CustomDialog'

import { useTranslation } from 'react-i18next'
import { CustomerContactType } from 'shared/store/useCustomers/types'
import { useNotificationContext } from 'shared/context/Notifications'
import {
   addContactToCustomerService,
   deleteContactService,
   editContactService,
} from 'views/Authorized/views/Customers/services'
import { isAxiosError } from 'axios'
import { useQueryClient } from 'react-query'
import DetailsModal from '../DetailsModal'
import DeleteModal from '../DeleteModal'

interface Props {
   modalDetails: ContactModalType
   customerUuid: string
   onClose: () => void
}

export default function Modal({ modalDetails, customerUuid, onClose }: Props) {
   const [isAddingContact, setIsAddingContact] = useState(false)
   const [isDeletingContact, setIsDeletingContact] = useState(false)
   const [isEditingContact, setIsEditingContact] = useState(false)
   const { t } = useTranslation(['TRANSLATION', 'CUSTOMERS'])
   const { addNotification } = useNotificationContext()
   const queryClient = useQueryClient()

   function editContact(values: CustomerContactType) {
      setIsEditingContact(true)
      editContactService(customerUuid, modalDetails.contactDetails!.uuid, values)
         .then(() => {
            addNotification(t('TRANSLATION:successSave'), 'success')
            queryClient.invalidateQueries('customers')
            onClose()
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
            } else {
               addNotification('failedSave', 'error')
            }
         })
         .finally(() => {
            setIsEditingContact(false)
         })
   }

   function addContact(values: CustomerContactType) {
      setIsAddingContact(true)
      const { uuid, ...rest } = values
      addContactToCustomerService(customerUuid, rest)
         .then(() => {
            addNotification(t('CUSTOMERS:modal.contactAdded'), 'success')
            queryClient.invalidateQueries('customers')

            onClose()
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
            } else {
               addNotification('failedSave', 'error')
            }
         })
         .finally(() => {
            setIsAddingContact(false)
         })
   }

   function deleteContact() {
      setIsDeletingContact(true)
      deleteContactService(customerUuid, modalDetails.contactDetails!.uuid!)
         .then(() => {
            addNotification(t('CUSTOMERS:modal.contactDeleted'), 'success')
            queryClient.invalidateQueries('customers')

            onClose()
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
            } else {
               addNotification('failedSave', 'error')
            }
         })
         .finally(() => {
            setIsDeletingContact(false)
         })
   }

   function renderContent() {
      switch (modalDetails.variant) {
         case 'CREATE':
            return (
               <DetailsModal
                  onClose={onClose}
                  onSubmit={(values) => addContact(values)}
                  isSubmitting={isAddingContact}
               />
            )
         case 'DELETE':
            return (
               <DeleteModal
                  contactName={`${modalDetails.contactDetails!.name} ${modalDetails.contactDetails!.surname}`}
                  onClose={onClose}
                  isDeleting={isDeletingContact}
                  onDelete={deleteContact}
               />
            )
         case 'EDIT':
            return (
               <DetailsModal
                  contactDetails={modalDetails.contactDetails}
                  onClose={onClose}
                  onSubmit={(values) => editContact(values)}
                  isSubmitting={isEditingContact}
               />
            )

         default:
            return null
      }
   }

   return (
      <CustomDialog
         maxWidth={modalDetails.variant === 'DELETE' ? undefined : 'lg'}
         isLoading={modalDetails.variant === undefined}
         deleteVariant={modalDetails.variant === 'DELETE'}
         open={modalDetails.open}
         disableClosing
         onClose={onClose}
         fullWidth
         content={renderContent()}
      />
   )
}
